/**
 * GFG-405 — the Gift Links table (prototype `cxLinksRender`): # · GIFT LINK · STATUS · REDEEMED ·
 * copy, and the search / status filters over it (`useGiftLinkFilters`, `GiftLinksToolbar`). The
 * table is presentational: the rows come already numbered and filtered (`toGiftLinkRows`,
 * `filterGiftLinks`), so a filtered row keeps the number it has in the campaign's full list (D5).
 * Same cells, chips, controls and loading/error states as the contacts table.
 *
 * A campaign can hold 10,000 links, so a keystroke or a toast must not re-render every row: the
 * table and its rows are memoized (stable rows and `onCopy` from the caller), the search filters
 * through a deferred value, and the copy button carries a native `title` instead of a Tooltip.
 */
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { Alert, Skeleton } from "@mui/material";
import { memo, type ReactNode, useDeferredValue, useEffect, useId, useMemo, useState } from "react";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import { ALL_STATUSES } from "@/constants/campaignContacts";
import { GIFT_LINKS_COPY } from "@/constants/giftLinks";
import { CONTACTS_TD_CLASS, CONTACTS_TH_CLASS } from "../contacts/ContactsDialog";
import { contactStatusesPresent } from "../contacts/campaignContacts-helper";
import { filterGiftLinks, formatRedeemedAt } from "./giftLinks-helper";
import type { GiftLinkViewRow } from "./types";

interface GiftLinksTableProps {
  rows: readonly GiftLinkViewRow[];
  /** The envelope request is in flight: skeleton rows instead of the table. */
  loading: boolean;
  /** A ready-to-read failure message, or null. */
  error: string | null;
  /** The campaign's zone (IANA): the Redeemed instant is read in it. */
  timeZone: string;
  /** Copies one row's link; omitted, no copy buttons. Never offered for a row without a URL. */
  onCopy?: (row: GiftLinkViewRow) => void;
}

const T = GIFT_LINKS_COPY;
const SKELETON_ROWS = [0, 1, 2, 3, 4] as const;
const COLUMN_COUNT = 5;

/** Prototype `.cmp-copy-btn`. */
const COPY_BUTTON_CLASS =
  "inline-flex shrink-0 cursor-pointer items-center justify-center rounded-md p-0.5 " +
  "text-rodeo-dust transition-colors hover:bg-wax hover:text-dark-dust";

/**
 * The link in a truncated monospace `<code>` whose full value stays reachable: `title` shows it on
 * hover and the text is real selectable DOM text (CSS truncation only), like the contacts `UrlCell`.
 */
function LinkCell({ url }: { url: string | null }) {
  if (url === null) {
    return <td className={`${CONTACTS_TD_CLASS} text-graphite`}>{T.emptyValue}</td>;
  }
  return (
    <td className={CONTACTS_TD_CLASS}>
      <span className="inline-flex max-w-[560px] items-center">
        <code
          title={url}
          className="min-w-0 truncate rounded-md bg-clear-dust px-2 py-1 font-mono text-[12.5px] text-graphite"
        >
          {url}
        </code>
      </span>
    </td>
  );
}

function CopyCell({
  row,
  onCopy,
}: {
  row: GiftLinkViewRow;
  onCopy?: (row: GiftLinkViewRow) => void;
}) {
  if (row.url === null || onCopy === undefined) return <td className={CONTACTS_TD_CLASS} />;
  return (
    <td className={`${CONTACTS_TD_CLASS} text-right`}>
      <button
        type="button"
        title={T.copyOne}
        aria-label={T.copyOneLabel(row.number)}
        onClick={() => onCopy(row)}
        className={COPY_BUTTON_CLASS}
      >
        <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
      </button>
    </td>
  );
}

/** Memoized: with stable `row` and `onCopy`, a row re-renders only when its own data changes. */
const GiftLinkRow = memo(function GiftLinkRow({
  row,
  timeZone,
  onCopy,
}: {
  row: GiftLinkViewRow;
  timeZone: string;
  onCopy?: (row: GiftLinkViewRow) => void;
}) {
  const redeemedTone = row.redeemedAt === null ? "text-graphite" : "text-rodeo-black";
  return (
    <tr className="last:[&>td]:border-b-0">
      <td className={`${CONTACTS_TD_CLASS} text-graphite`}>{row.number}</td>
      <LinkCell url={row.url} />
      <td className={CONTACTS_TD_CLASS}>
        <CampaignStatusChip status={row.status} />
      </td>
      <td className={`${CONTACTS_TD_CLASS} whitespace-nowrap ${redeemedTone}`}>
        {formatRedeemedAt(row.redeemedAt, timeZone)}
      </td>
      <CopyCell row={row} onCopy={onCopy} />
    </tr>
  );
});

function LoadingRows() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={T.loadingLabel}
      className="mt-4 flex flex-col gap-2"
    >
      {SKELETON_ROWS.map((row) => (
        <Skeleton key={row} variant="rounded" height={40} />
      ))}
    </div>
  );
}

function TableHead() {
  return (
    <thead>
      <tr>
        <th className={`${CONTACTS_TH_CLASS} w-14`}>{T.columns.number}</th>
        <th className={CONTACTS_TH_CLASS}>{T.columns.link}</th>
        <th className={CONTACTS_TH_CLASS}>{T.columns.status}</th>
        <th className={CONTACTS_TH_CLASS}>{T.columns.redeemed}</th>
        <th className={CONTACTS_TH_CLASS}>
          {/* The prototype's header cell is blank; the words stay for screen readers. */}
          <span className="sr-only">{T.copyOne}</span>
        </th>
      </tr>
    </thead>
  );
}

function GiftLinksTable({ rows, loading, error, timeZone, onCopy }: GiftLinksTableProps) {
  if (loading) return <LoadingRows />;

  if (error !== null) {
    return (
      <div className="mt-4">
        <Alert severity="error">{error || T.errorFallback}</Alert>
      </div>
    );
  }

  return (
    <div className="mt-4 max-h-[56vh] overflow-auto border-t border-smoke">
      <table className="w-full border-collapse">
        <TableHead />
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={COLUMN_COUNT} className="px-2.5 py-6 text-center text-graphite">
                {T.empty}
              </td>
            </tr>
          )}
          {rows.map((row) => (
            <GiftLinkRow key={row.id} row={row} timeZone={timeZone} onCopy={onCopy} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Memoized: a toast or the urgent half of a keystroke leaves `rows` as they were, so it skips. */
export default memo(GiftLinksTable);

// ---- the filters over it -------------------------------------------------------------------------

/**
 * The search and status filters over `rows`, and the rows they leave. Each opening starts clean: a
 * search left over from the previous visit would silently hide links.
 */
export function useGiftLinkFilters(rows: readonly GiftLinkViewRow[], open: boolean) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(ALL_STATUSES);
  useEffect(() => {
    if (open) {
      setSearch("");
      setStatusFilter(ALL_STATUSES);
    }
  }, [open]);
  // The input follows `search` at once; the 10,000-row filter follows it when the typing lets up.
  const deferredSearch = useDeferredValue(search);
  const statuses = useMemo(() => contactStatusesPresent(rows), [rows]);
  // The selected status only counts while the rows still contain it (a refetch can drop it).
  const activeStatus = statuses.find((s) => s === statusFilter) ?? null;
  const filtered = useMemo(
    () => filterGiftLinks(rows, deferredSearch, activeStatus),
    [rows, deferredSearch, activeStatus],
  );
  return { search, setSearch, statusFilter, setStatusFilter, statuses, activeStatus, filtered };
}

export type GiftLinkFilters = ReturnType<typeof useGiftLinkFilters>;

/** "Search links", the "All statuses" select of the statuses present, and `end` at the right. */
export function GiftLinksToolbar({ filters, end }: { filters: GiftLinkFilters; end: ReactNode }) {
  const statusId = useId();
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2.5">
      <div className="flex max-w-[320px] flex-1 items-center gap-1.5 rounded-lg border border-smoke bg-white px-2.5 py-[7px] focus-within:border-dark-dust">
        <SearchIcon sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
        <input
          type="search"
          value={filters.search}
          onChange={(event) => filters.setSearch(event.target.value)}
          placeholder={T.searchPlaceholder}
          aria-label={T.searchLabel}
          className="w-full min-w-0 bg-transparent text-[13px] text-rodeo-black outline-none placeholder:text-graphite"
        />
      </div>
      <label
        htmlFor={statusId}
        className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.5px] text-graphite"
      >
        {T.statusLabel}
        <select
          id={statusId}
          value={filters.statusFilter}
          onChange={(event) => filters.setStatusFilter(event.target.value)}
          className="rounded-lg border border-smoke bg-white px-2.5 py-[7px] text-[13px] font-normal normal-case tracking-normal text-rodeo-black focus:border-dark-dust focus:outline-none"
        >
          <option value={ALL_STATUSES}>{T.allStatuses}</option>
          {filters.statuses.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      {end !== null && <div className="ml-auto">{end}</div>}
    </div>
  );
}
